import {
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  Input,
  Label,
  Modal,
} from '@/components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, isEqual, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components/PageTitle'
import {
  Color,
  Gradient,
  Languages,
  Stage,
  StagePayload,
  Stages,
} from '../../common/models'
import { StageTranslation } from '../../common/models/stage'
import FormGroup from '../../core/components/FormGroup'
import { FormUploadPreview } from '../../core/components/FormUploadPreview'
import { formatDateTime } from '../../core/constants/times'
import { getSettingsColors } from '../../core/services/festival-settings'
import {
  createStages,
  deleteStages,
  getAllStages,
  updateStages,
} from '../../core/services/stage'
import { uploadImage } from '../../core/services/upload-image'
import { ColorCircle } from './ColorCircle'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import SelectField from '@/components/select-field'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircleIcon } from 'lucide-react'
import { AlertRatio } from '@/components/AlertRatio'
import { Icon } from '@/common/components/Icon'

export interface CreateStageModalProps {
  showEditModal: boolean
  stage?: Stage
  setShowEditModal: (show: boolean) => void
  setStages: (s: Stages[]) => void
  sortBy?: string
  size: number
  isEditMode: boolean
  setIsEditMode: (e: boolean) => void
  setLoading: (e: boolean) => void
  allLanguages: Languages[]
}

interface IStageValues {
  content: StageTranslation[]
  fontColor: string
  color: string
  ordinal: number
  published: boolean
  wrapper: boolean
  defaultImage?: boolean
  photo?: string
  publishedDate?: string
}

export const CreateStageModal = (props: CreateStageModalProps): JSX.Element => {
  const {
    showEditModal,
    setShowEditModal,
    stage,
    setStages,
    sortBy,
    size,
    isEditMode,
    setIsEditMode,
    setLoading,
    allLanguages,
  } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showDeleteStageConfirmationModal, setDeleteStageConfirmationModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [aStageIsDefault, setAStageIsDefault] = useState<string | undefined>(
    undefined,
  )
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [gradients, setGradients] = useState<Gradient[]>()
  const [colors, setColors] = useState<Color[]>()
  const [content, setContent] = useState<StageTranslation[]>([])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const response = await getAllStages({ sortBy, size })
        const findDefault = find(response.data.content, cont => cont.wrapper === true)
        if (findDefault !== undefined) {
          setAStageIsDefault(findDefault.name)
        }

        const response2 = await getSettingsColors()
        const main = find(response2.data, data => data.name === 'PRIMARY')
        const secondary = find(response2.data, data => data.name === 'SECONDARY')
        if (secondary?.gradients && main?.gradients) {
          setGradients([...secondary?.gradients, ...main?.gradients])
        }
        if (main?.colors) {
          setColors(main.colors)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const temp = map(allLanguages, (lang) => {
          return {
            locale: lang.locale,
            name: '',
            subheading: '',
          }
        })
        setValue('content', temp)
        if (!!stage && isEditMode) {
          if (!!stage.translations && stage.translations.length > 0) {
            setContent(stage.translations)
            setTimeout(() => {
              map(stage.translations, trans => {
                const index = findIndex(
                  allLanguages,
                  (l) => l.locale === trans.locale,
                )
                if (index !== -1) {
                  setValue(`content.${index}.locale`, trans.locale)
                  setValue(`content.${index}.subheading`, trans.subheading)
                  setValue(`content.${index}.name`, trans.name)
                }
              })
            }, 100)
          }
          setValue('fontColor', stage.fontColor, { shouldValidate: true })
          setValue('ordinal', stage.ordinal, { shouldValidate: true })
          if (stage.publishedAt) {
            setValue(
              'publishedDate',
              formatDateTime(stage.publishedAt),
              {
                shouldValidate: true,
              },
            )
          }
          setValue('color', stage.gradient?.name, { shouldValidate: true })
          setValue('published', stage.published, { shouldValidate: true })
          setValue('defaultImage', stage.defaultImage, { shouldValidate: true })
          setValue('photo', stage.imageLink, { shouldValidate: true })
          setImage(stage.imageLink)
          if (stage?.imageLink) {
            const url = new URL(stage?.imageLink)
            const i = url?.pathname?.lastIndexOf('/') + 1
            const imageName = url?.pathname?.substring(i)
            setImageName(imageName)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
    return () => {
      setImage('')
      setImageName('')
      reset()
    }
  }, [stage, isEditMode])

  const handleClose = () => {
    setShowEditModal(false)
    setIsEditMode(false)
    setImage('')
    setImageName('')
    reset()
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
        subheading: '',
      }
    })
    setValue('content', temp)
  }

  const deleteStage = async () => {
    try {
      setLoading(true)
      if (stage) {
        const { status } = await deleteStages([stage.id])
        const response = await getAllStages({ sortBy, size })
        setStages(response.data.content)
        if (status === 204 && response.status === 200) {
          handleClose()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const schema = yup.object().shape({
    content: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          name: yup.string().required('Field is required'),
          subheading: yup.string().required('Field is required'),
        }),
      )
      .required(),
    fontColor: yup.string().required('Field is required'),
    color: yup.string().required('Field is required'),
    ordinal: yup.number().required('Field is required'),
    published: yup.boolean().required('Field is required'),
    wrapper: yup.boolean().required('Field is required'),
    publishedDate: yup.string(),
    defaultImage: yup.boolean(),
    photo: yup.string().when('defaultImage', (defaultImage) => {
      if (defaultImage) {
        return yup.string().notRequired()
      } else {
        return yup.string().required('Field is required')
      }
    }),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
    control,
  } = useForm<IStageValues>({
    mode: 'onChange',
    resolver: yupResolver<IStageValues>(schema),
    defaultValues: {
      fontColor: '',
      color: '',
      ordinal: 0,
      publishedDate: formatDateTime(new Date().valueOf()),
      published: false,
      defaultImage: false,
      photo: '',
      wrapper: false,
    },
  })

  const contentWatch = watch('content')
  const defaultImageWatch = watch('defaultImage')
  const fontColorWatch = watch('fontColor')
  const colorWatch = watch('color')

  const submit = async (values: IStageValues) => {
    setLoading(true)
    setIsSubmitting(true)
    const defaultTime = new Date()
    defaultTime.setHours(0)
    defaultTime.setMinutes(0)
    const published = values.publishedDate
      ? new Date(values.publishedDate).getTime()
      : defaultTime.getTime()

    const data: StagePayload = {
      translations: values.content,
      ordinal: values.ordinal,
      publishedAt: published,
      published: values.published,
      defaultImage: values.defaultImage || false,
      fontColor: values.fontColor,
      gradientId: find(gradients, (c) => c.name === values.color)?.id,
      wrapper: values.wrapper,
    }

    if (values.photo) {
      data.imageLink = values.photo
    }
    if (stage?.id) {
      try {
        const { status } = await updateStages(stage.id, data)
        const response = await getAllStages({ sortBy, size })
        setStages(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    } else {
      try {
        const { status } = await createStages(data)
        const response = await getAllStages({ sortBy, size })
        setStages(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    }
    handleClose()
    setLoading(false)
    setIsSubmitting(false)
  }

  return (
    <>
      <Modal
        open={showEditModal}
        onOpenChange={handleClose}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="stages-bold"
              title={
                stage ? (
                  <span>
                    edit <span className="text-accent-1">Stage</span>
                  </span>
                ) : (
                  <span>
                    Add New <span className="text-accent-1">Stage</span>
                  </span>
                )
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-between">
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteStageConfirmationModal(true)
              }}
            >
              Delete
              <Icon
                icon="close-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
            <div className="flex w-full items-center gap-2 justify-end mt-4">
              <Button onClick={handleClose} variant="secondary" type="button">
                Cancel
              </Button>
              <Button
                form="stage-form"
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={
                  isUploading ||
                  (Object.keys(dirtyFields).length === 0 &&
                    isEqual(contentWatch, content)) ||
                  (isErrorRatio && !defaultImageWatch)
                }
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="stage-form"
          className="w-full"
          onSubmit={handleSubmit(submit)}
        >
          <div className="space-y-7 w-full">
            <h2 className="text-title-xs font-semibold text-monochrome-1">
              Main Info
            </h2>
            {map(allLanguages, (language, index) => {
              return (
                <Card
                  className="p-5 space-y-[25px] bg-monochrome-120"
                  key={language.id}
                >
                  <FormGroup label="Language">
                    <Input value={language.name} readOnly />
                  </FormGroup>
                  <Input
                    hidden={true}
                    {...register(`content.${index}.locale`)}
                    value={language.locale}
                    readOnly
                  />
                  <FormGroup label="Name">
                    <Input
                      isInvalid={!!errors?.content?.[index]?.name?.message}
                      {...register(`content.${index}.name`)}
                      type="text"
                      placeholder="Enter Name"
                    />
                  </FormGroup>
                  <FormGroup label="Subheading">
                    <Input
                      isInvalid={
                        !!errors?.content?.[index]?.subheading?.message
                      }
                      {...register(`content.${index}.subheading`)}
                      type="text"
                      placeholder="Enter Subheading"
                    />
                  </FormGroup>
                </Card>
              )
            })}

            <Controller
              name="fontColor"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Font Color"
                  outline
                  placeholder="Select Font Color"
                  error={errors?.fontColor?.message}
                  options={map(colors, (color) => ({
                    label: (
                      <div className="flex items-center gap-2">
                        <ColorCircle
                          color1={find(colors, (c) => c.name === color.name)}
                          color2={find(colors, (c) => c.name === color.name)}
                        />
                        {color.name}
                      </div>
                    ),
                    value: color.name,
                  }))}
                  onValueChange={field.onChange}
                  {...field}
                />
              )}
            />
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Color"
                  outline
                  placeholder="Select Color"
                  error={errors?.color?.message}
                  options={map(gradients, (gradient) => ({
                    label: (
                      <div className="flex items-center gap-2">
                        <ColorCircle
                          gradient={find(
                            gradients,
                            (g) => g.name === gradient.name,
                          )}
                        />
                        {gradient.name}
                      </div>
                    ),
                    value: gradient.name,
                  }))}
                  onValueChange={field.onChange}
                  {...field}
                />
              )}
            />

            <FormGroup label="Ordinal">
              <Input
                isInvalid={!!errors?.ordinal?.message}
                {...register('ordinal')}
                type="number"
                min={0}
                placeholder="Enter Ordinal"
              />
            </FormGroup>

            <Controller
              name="publishedDate"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : undefined)
                  }
                  hasError={!!errors?.publishedDate?.message}
                  label="Publish Date & Time"
                  placeholder="Enter Publish Date"
                />
              )}
            />

            <div className="flex flex-col space-y-2">
              <Controller
                name="published"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Published"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'published',
                    }}
                  >
                    <Checkbox
                      id="published"
                      className="border-monochrome-1"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                  </FormGroup>
                )}
              />

              <Controller
                name="wrapper"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Default Stage"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'defaultStage',
                    }}
                  >
                    <Checkbox
                      id="defaultStage"
                      className="border-monochrome-1"
                      disabled={aStageIsDefault !== undefined}
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                  </FormGroup>
                )}
              />
            </div>
            {aStageIsDefault && (
              <Alert
                variant="default"
                className="bg-monochrome-120 border-monochrome-120 text-monochrome-1"
                title={`Attention! ${aStageIsDefault} is already Set as Default Stage. Ther can only be one.`}
              >
                <AlertCircleIcon className="size-4 text-monochrome-1" />
                <AlertTitle className="text-body-medium text-monochrome-1">
                  Attention!
                </AlertTitle>
                <AlertDescription className="text-body-medium text-monochrome-1">
                  {`${aStageIsDefault} is already Set as Default Stage. Ther can only be one.`}
                </AlertDescription>
              </Alert>
            )}

            <hr className="my-[25px] h-px w-full border-monochrome-120" />

            <div className="flex flex-col space-y-[25px]">
              <h2 className="text-2xl font-semibold text-monochrome-1">
                Photo
              </h2>

              <Controller
                name="defaultImage"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Default Image"
                    labelProps={{
                      htmlFor: 'defaultImage',
                    }}
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      id="defaultImage"
                      className="border-monochrome-1"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                  </FormGroup>
                )}
              />

              <FormUploadPreview
                formGroupProps={{
                  direction: 'column',
                }}
                control={
                  <Input
                    multiple={false}
                    disabled={defaultImageWatch}
                    accept="image/*"
                    isInvalid={
                      !!errors?.photo?.message || invalidPhoto || isErrorRatio
                    }
                    name="photo"
                    onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                      const file = e?.target?.files?.[0]
                      if (file) {
                        if (file.size > 1_000_000) {
                          setInvalidPhoto(true)
                          setIsError(true)
                          setShowMessageBox(true)
                          setMessage('The photo is too large (1 MB)')
                          return
                        }
                        const data = await uploadImage(
                          file,
                          setIsUploading,
                          setIsError,
                          setShowMessageBox,
                          setMessage,
                          setIsErrorRatio,
                          'STAGE',
                        )
                        if (data.photoUrl) {
                          setImage(data.photoUrl)
                          setImageName(file.name)
                          setValue('photo', data.photoUrl, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      }
                    }}
                    type="file"
                  />
                }
                imageName={imageName}
                direction="row"
                isUploading={isUploading}
                disabled={defaultImageWatch}
                url={image}
                onRemove={() => {
                  setImage('')
                  setImageName('')
                  setValue('photo', '')
                }}
                imageStyle={{
                  objectFit: 'contain',
                }}
                label="Photo (32X32)"
                previewLabel="Photo (32X32)"
              />
            </div>
            {isErrorRatio && <AlertRatio />}
          </div>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteStageConfirmationModal}
        setDeleteConfirmationModal={setDeleteStageConfirmationModal}
        deleteTitle={'Stage'}
        deleteFunc={deleteStage}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
